import React, { useEffect, useRef, useState } from 'react';
import { FaSearch, FaAngleLeft, FaAngleRight, FaFilter } from 'react-icons/fa';
import { FaArrowLeftLong } from 'react-icons/fa6';
import { MdAdd } from 'react-icons/md';
import {
  PiDotsThreeOutlineVerticalFill,
  PiSortAscendingBold,
} from 'react-icons/pi';
import { IoIosArrowDown } from 'react-icons/io';
import {
  FiMail,
  FiClock,
  FiArchive,
  FiTrash2,
  FiArrowRightCircle,
  FiLink,
} from 'react-icons/fi';
import { RiMailAddFill } from 'react-icons/ri';
import { TiThMenu } from 'react-icons/ti';
import { IoClose } from 'react-icons/io5';
import toast from 'react-hot-toast';

import BaseLayout from '../../components/layout/BaseLayout';
import Button from '../../components/custom/Button';
import Modal from '../../components/Modal';
import Integration from '../../components/Integration';
import EmailLabels from '../../components/dialog/EmailLabels';
import EmailSorting from '../../components/dialog/EmailSorting';
import EmailComponent from '../../components/dialog/EmailModal';
import EmailSearch from '../../components/dialog/EmailSearch';
import ComposeEmail from '../../components/dialog/ComposeEmail';
import NoMailScreen from '../../components/dialog/NoMailScreen';
import MailBoxesIntegration from '../../components/dialog/MailBoxesIntegration';
import CombinedFeedback from '../../components/meeting/CombinedFeedback';
import CalendarEventEditor from '../../components/meeting/CalendarEventEditor';
import QuestionForm from '../../components/form/QuestionForm';
import SelectedTriggerItem from '../../components/form/SelectedTriggerItem';
import TriggerModal from '../../components/form/TriggerModal';
import { taskDateFormat } from '../../utils/helper';
import {
  dataSources,
  dialogueData,
  integrationData,
  meetingFeedbackData,
} from '../../utils/constants';
import { createTempBot } from '../../services/api';
import { DataState } from '../../context/DataProvider';
import { useEmails } from '../../context/EmailsProvider';
import useClickOutside from '../../hooks/useClickOutside';
import PremiumFeatures from '../../components/premiumFeatureComponent/PremiumFeature';

const analyticsData = {
  snippet:
    'This email is an application for the Python Developer Intern position. The applicant highlights their experience with Python, Flask, MongoDB, and AWS.',
  priority: 'High',
  insights:
    'The email demonstrates strong alignment with the job requirements and showcases relevant technical skills.',
};

function getDayMonthAndISTTime(dateString) {
  // Convert to Date object
  const date = new Date(dateString);

  // Convert to IST (UTC+5:30)
  const options = {
    timeZone: 'Asia/Kolkata',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  };
  const istTime = new Intl.DateTimeFormat('en-IN', options).format(date);

  // Format day and month
  const day = date.getDate(); // Day of the month
  const month = date.toLocaleString('en-IN', { month: 'short' }); // Short month name

  return `${istTime}, ${day} ${month}`;
}

const convertOutlookToUniformFormat = (outlookEmailList) => {
  return outlookEmailList.map((outlookMail) => {
    return {
      ...outlookMail,
      snippet: outlookMail.bodyPreview,
      // priority: outlookMail.importance,
    };
  });
};

const EmailList = ({
  emailList,
  loadingEmails,
  emailsPerPage,
  isOutlook = false,
  setSelectedEmail,
  isGmailSynchronized,
  isOutlookSynchronized,
}) => {
  useEffect(() => {
    if (isOutlook && !isOutlookSynchronized) {
      toast.error(
        'Outlook is not synchronized. Please synchronize to view emails.',
      );
    } else if (!isOutlook && !isGmailSynchronized) {
      toast.error(
        'Gmail is not synchronized. Please synchronize to view emails.',
      );
    }
  }, [isGmailSynchronized, isOutlookSynchronized, isOutlook]);
  const [hoveredRow, setHoveredRow] = useState(null);
  const uniformEmailList = isOutlook
    ? convertOutlookToUniformFormat(emailList)
    : emailList;

  if (
    (isOutlook && !isOutlookSynchronized) ||
    (!isOutlook && !isGmailSynchronized)
  ) {
    return (
      <div className="flex h-full w-full flex-1 flex-col gap-2">
        <div className="mx-auto flex h-full w-full flex-col items-center overflow-hidden ">
          <NoMailScreen
            isOutlook={isOutlook}
            isGmailSynchronized={isGmailSynchronized}
            isOutlookSynchronized={isOutlookSynchronized}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-1 flex-col gap-2">
      <div className="mx-auto flex h-full w-full flex-col items-center overflow-hidden">
        {/* TABLE VIEW FOR DESKTOP */}
        <div className="scrollbar-none hidden w-full overflow-x-auto overflow-y-auto rounded-b-xl border border-zinc-700 bg-zinc-900 text-zinc-100 shadow-md md:block">
          <table className="min-w-full border-collapse divide-y divide-zinc-700 rounded-b-lg">
            <thead>
              <tr className="bg-zinc-800">
                {['Sender', 'Snippet', 'Time', 'Priority'].map((heading) => (
                  <th
                    key={heading}
                    className="px-3 py-2 text-left text-sm font-medium text-zinc-400"
                  >
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-700">
              {loadingEmails ? (
                Array.from({ length: emailsPerPage || 10 }).map((_, index) => (
                  <tr key={index} className="animate-pulse">
                    <td className="px-3 py-2">
                      <div className="h-6 w-36 rounded bg-gray-800"></div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="h-6 w-[450px] rounded bg-gray-800"></div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="h-6 w-16 rounded bg-gray-800"></div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="h-6 w-12 rounded bg-gray-800"></div>
                    </td>
                  </tr>
                ))
              ) : emailList?.length === 0 ? (
                <tr>
                  <td colSpan="4" className="py-4 text-center text-gray-500">
                    No Emails Found
                  </td>
                </tr>
              ) : (
                uniformEmailList.map((email, index) => {
                  const senderName = email.from?.name;
                  const subject = email?.subject || '';
                  const snippet = email?.snippet || '';
                  const displaySnippet =
                    subject.length >= 100
                      ? subject.slice(0, 100)
                      : `${subject} ${snippet.slice(0, 100 - subject.length)}`.trim() +
                        '...';

                  return (
                    <tr
                      key={index}
                      className="cursor-pointer hover:bg-zinc-800"
                      onMouseEnter={() => setHoveredRow(index)}
                      onMouseLeave={() => setHoveredRow(null)}
                      onClick={() => setSelectedEmail(email)}
                    >
                      <td
                        className={`flex items-center whitespace-nowrap px-3 py-2 text-sm ${!email.isRead ? 'font-bold' : 'text-gray-300'}`}
                      >
                        <FiMail className="mr-2 text-[#5652CF]" />
                        {senderName?.length > 20
                          ? `${senderName.slice(0, 20)}...`
                          : senderName}
                      </td>
                      <td
                        className={`px-3 py-2 text-sm text-zinc-400 ${!email.isRead ? 'font-bold' : 'font-semibold'}`}
                      >
                        {displaySnippet}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-sm text-zinc-500">
                        {hoveredRow === index ? (
                          <div className="flex items-center space-x-2 font-semibold">
                            <FiArchive
                              className="hover:text-zinc-400"
                              size={15}
                            />
                            <FiTrash2
                              className="hover:text-zinc-400"
                              size={15}
                            />
                            <FiArrowRightCircle
                              className="hover:text-zinc-400"
                              size={15}
                            />
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <FiClock className="mr-2 text-zinc-500" />
                            {getDayMonthAndISTTime(email.date)}
                          </div>
                        )}
                      </td>
                      <td className="flex items-center justify-center px-3 py-2 text-sm">
                        <span
                          className={`flex w-5/6 items-center justify-center rounded-xl bg-opacity-80 px-2 py-[2px] text-xs font-semibold text-white
                        ${
                          email?.priority <= 1
                            ? 'bg-[#5652CF]'
                            : email?.priority <= 3
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                        }`}
                        >
                          {email?.priority <= 1
                            ? 'Low'
                            : email?.priority <= 3
                              ? 'Medium'
                              : 'High'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* CARD VIEW FOR MOBILE */}
        <div className="block w-full divide-y divide-zinc-700 overflow-y-auto border border-zinc-700 md:hidden">
          {loadingEmails ? (
            Array.from({ length: 10 }).map((_, index) => (
              <div
                key={index}
                className="animate-pulse space-y-1 bg-zinc-900 p-2"
              >
                <div className="h-4 w-3/4 rounded bg-zinc-700"></div>
                <div className="flex w-full items-center justify-between gap-2">
                  <div className="h-3 w-1/2 rounded bg-zinc-800"></div>
                  <div className="h-3 w-10 rounded bg-zinc-700"></div>
                </div>
              </div>
            ))
          ) : uniformEmailList.length === 0 ? (
            <div className="py-4 text-center text-gray-500">
              No Emails Found
            </div>
          ) : (
            uniformEmailList.map((email, index) => (
              // eslint-disable-next-line
              <div
                key={index}
                onClick={() => setSelectedEmail(email)}
                className="flex cursor-pointer flex-col space-y-1 bg-zinc-900 p-3 shadow-md hover:bg-zinc-800"
              >
                <div className="flex items-center justify-between gap-4">
                  <span
                    className={`flex items-center justify-center gap-2 text-[0.8rem] font-semibold  sm:text-sm ${!email.isRead ? 'text-white' : 'text-zinc-300'}`}
                  >
                    <FiMail className="mr-1 text-[#5652CF]" />
                    {email.from?.name?.slice(0, 25) || 'Unknown'}
                  </span>
                  <span className="flex items-center text-[0.6rem] text-zinc-400 sm:text-xs">
                    <FiClock className="mr-1" />
                    {getDayMonthAndISTTime(email.date)}
                  </span>
                </div>
                <div
                  className="flex justify-between gap-6"
                  style={{ fontSize: '0.6rem' }}
                >
                  <div className="text-xs font-medium text-zinc-400">
                    {email.subject || email.snippet}
                  </div>
                  <div className="">
                    <span
                      className={`rounded-xl px-2 py-[2px] font-semibold text-white
                  ${
                    email?.priority <= 1
                      ? 'bg-[#5652CF]'
                      : email?.priority <= 3
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                  }`}
                    >
                      {email?.priority <= 1
                        ? 'Low'
                        : email?.priority <= 3
                          ? 'Medium'
                          : 'High'}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

const CreateDialogueForm = ({ isModalOpen, closeModal }) => {
  const [isIntegrationModalOpen, setIsIntegrationModalOpen] = useState(false);
  const [isDataSourceModalOpen, setIsDataSourceModalOpen] = useState(false);

  const [selectTrigerOptions, setSelectTrigerOptions] = useState([]);
  const [selectIntegrationOptions, setSelectIntegrationOptions] = useState([]);
  const [selectDataSourceOptions, setSelectDataSourceOptions] = useState([]);
  const {
    createAndHandleClarificationQuestions,
    setError,
    botCreationId,
    setBotCreationId,
    userGoogleId,
  } = DataState();
  const [editOption, setEditOption] = useState(null);
  const [triggerName, setTriggerName] = useState(''); // State for storing the trigger name
  const [dialogueName, setDialogueName] = useState(''); // State for storing the instruction
  const [instruction, setInstruction] = useState(''); // State for storing the instruction

  useEffect(() => {
    if (isModalOpen) {
      const createBot = async () => {
        try {
          const response = await createTempBot({}, userGoogleId);
          if (response.error) {
            setError(JSON.stringify(response.error));
            return;
          }
          if (response.bot_id) {
            setBotCreationId(response.bot_id);
          }
        } catch (err) {
          setError(JSON.stringify(err));
        }
      };
      createBot();
    }
    // eslint-disable-next-line
  }, [isModalOpen]);

  const handleDeleteIntegrationOption = (optionId) => {
    setSelectIntegrationOptions((prev) =>
      prev.filter((item) => item.id !== optionId),
    );
  };
  const handleDeleteDataSourceOption = (optionId) => {
    setSelectDataSourceOptions((prev) =>
      prev.filter((item) => item.id !== optionId),
    );
  };
  const handleIntergrationEditOption = (option) => {
    setEditOption(option);
    setIsIntegrationModalOpen(true);
  };
  const handleDataSourceEditOption = (option) => {
    setEditOption(option);
    setIsDataSourceModalOpen(true);
  };

  const [showQuestionForm, setShowQuestionForm] = useState();
  const [showTestScreen, setShowTestScreen] = useState();
  const [isFormLoading, setIsFormLoading] = useState(false);
  const clearStates = () => {
    setSelectTrigerOptions([]);
    setSelectIntegrationOptions([]);
    setSelectDataSourceOptions([]);
    setTriggerName('');
    setInstruction('');
  };
  const handleNewInstructionSubmission = async () => {
    setIsFormLoading(true);
    try {
      const final_response = await createAndHandleClarificationQuestions(
        instruction,
        triggerName,
        selectTrigerOptions,
        selectIntegrationOptions,
        selectDataSourceOptions,
      );
      if (final_response && final_response.questions) {
        setShowQuestionForm(true);
      } else {
        toast.error(
          'No clarification questions were generated. Please try rephrasing your instructions.',
        );
      }
    } catch (error) {
    } finally {
      setIsFormLoading(false);
    }
  };
  const handleShowTestScreen = () => {
    setShowTestScreen(true);
  };

  return (
    <div className="flex items-center justify-center duration-200">
      {!showTestScreen && !showQuestionForm && (
        <div className="bg-2 w-full overflow-auto rounded-lg">
          <div className="flex flex-col gap-8 p-6">
            <div className="flex flex-col gap-1">
              <div className="flex flex-col gap-1">
                <h1 className="text-base font-semibold">
                  Name of the PM/Dialogue
                </h1>
              </div>
              <input
                value={dialogueName}
                onChange={(e) => setDialogueName(e.target.value)} // Update instruction state
                className="bg-2 br-1 resize-none rounded-lg border bg-transparent px-4 py-2 text-base outline-none"
                placeholder=""
              />
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex flex-col gap-1">
                <h1 className="text-base font-semibold">
                  Dialogue instructions
                </h1>
              </div>
              <textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)} // Update instruction state
                className="bg-2 br-1 h-52 resize-none rounded-lg border bg-transparent px-4 py-2 text-base outline-none"
                placeholder="Instructions describe your workflow requirements in detail. Include all necessary steps, data processing instructions, and desired outcomes across integrated applications (e.g. Jira, Email, Sheets, Docs). Example: 'For every email received in the inbox, send an automated apology response explaining a 34 hour delay in support. Then, create a Jira task under the 'Support' Project, including the email content and tagging it as 'High Priority'. Notify the team lead in Slack once task is created."
              />
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col gap-1 ">
                  <h1 className="text-base font-semibold">App integrations</h1>
                  <p className="text-sm">
                    Add applications included in the workflow
                  </p>
                </div>
                <button
                  onClick={() => setIsIntegrationModalOpen(true)}
                  className="bg-1 p-2 text-sm transition-all duration-200"
                >
                  <MdAdd />
                </button>
              </div>
              {selectIntegrationOptions.map((item) => (
                <SelectedTriggerItem
                  item={item}
                  handleEditOption={() => handleIntergrationEditOption(item)}
                  handleDeleteOption={handleDeleteIntegrationOption}
                />
              ))}
            </div>
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-3">
                <div className="flex flex-col gap-1 ">
                  <h1 className="text-base font-semibold">Data sources</h1>
                  <p className="text-sm">
                    Add data sources required in the workflow
                  </p>
                </div>
                <button
                  onClick={() => setIsDataSourceModalOpen(true)}
                  className="bg-1 p-2 text-sm transition-all duration-200"
                >
                  <MdAdd />
                </button>
              </div>
              {selectDataSourceOptions.map((item) => (
                <SelectedTriggerItem
                  item={item}
                  handleEditOption={() => handleDataSourceEditOption(item)}
                  handleDeleteOption={handleDeleteDataSourceOption}
                />
              ))}
            </div>

            <div className="flex justify-end">
              <Button
                loading={isFormLoading}
                onClick={handleNewInstructionSubmission}
              >
                Continue
              </Button>
            </div>
            {botCreationId && (
              <TriggerModal
                title="Add integration"
                desc=" Select the app you’d like to add as integration."
                isOpen={isIntegrationModalOpen}
                onClose={() => {
                  setIsIntegrationModalOpen(false);
                  setEditOption(null);
                }}
                appsData={integrationData}
                selectedOptions={selectIntegrationOptions}
                setSelectedOptions={setSelectIntegrationOptions}
                editOption={editOption}
                botCreationId={botCreationId}
              />
            )}
            {botCreationId && (
              <TriggerModal
                title="Add data source"
                desc=" Select the app you’d like to add the data source from."
                isOpen={isDataSourceModalOpen}
                onClose={() => {
                  setIsDataSourceModalOpen(false);
                  setEditOption(null);
                }}
                appsData={dataSources}
                selectedOptions={selectDataSourceOptions}
                setSelectedOptions={setSelectDataSourceOptions}
                editOption={editOption}
                botCreationId={botCreationId}
              />
            )}
          </div>
        </div>
      )}
      {showQuestionForm && !showTestScreen && (
        <div className="h-[90%] w-[90%] max-w-3xl overflow-auto rounded-lg bg-zinc-900">
          <div className="flex justify-between gap-3 border-b border-zinc-800 px-3 py-5">
            <div className="flex items-center gap-3 bg-transparent px-3 py-1.5 text-xl font-bold">
              <button
                onClick={() => setShowQuestionForm(false)}
                className="bg-2 rounded-lg p-1.5 transition-all duration-200"
              >
                <FaArrowLeftLong />
              </button>
              Clarification Questions
            </div>
            <div></div>
          </div>
          <div className="flex flex-col gap-8 p-6">
            <QuestionForm
              handleShowTestScreen={handleShowTestScreen}
              closeModal={closeModal}
              setShowQuestionForm={setShowQuestionForm}
              clearStates={clearStates}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const DialogueDetails = ({
  handleSelectTab,
  selectedTab,
  isAnalyticsView,
  setIsAnalyticsView,
  setEmailStats,
  emailStats,
  selectedEmail,
  setSelectedEmail,
  isComposeEmailModalOpen,
  setIsComposeEmailModalOpen,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [expandedTile, setExpandedTile] = useState(null); // State for expanded tile
  const { userGoogleId } = DataState();
  const walletAddressRef = useRef(null);
  const [showIntegration, setShowIntegration] = useState(false);
  const {
    emails,
    totalEmails,
    currentPage,
    setCurrentPage,
    emailfilter,
    setEmailFilter,
    loadingEmails,
    emailsPerPage,
    totalPages,
    isOutlook,
    setIsOutlook,
    isGmailAuthenticated,
    isOutlookAuthenticated,
    isGmailSynchronized,
    isOutlookSynchronized,
  } = useEmails();
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [ismdFilterModalOpen, setIsmdFilterModalOpen] = useState(false);
  const [isSortingModalOpen, setIsSortingModalOpen] = useState(false);
  const [ismdSortingModalOpen, setIsmdSortingModalOpen] = useState(false);
  const [isSearchBoxOpen, setIsSearchBoxOpen] = useState(false);
  const [emailSorting, setEmailSorting] = useState('');
  const [meetingSubTab, setMeetingSubTab] = useState('completed');
  const [isAdvanceSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [showPremium, setShowPremium] = useState(true);

  // ################ states for responsive design ################
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  const nextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  const toggleDropdown = (botId) => {
    setDropdownOpen((prev) => (prev === botId ? null : botId));
  };

  const toggleExpand = (id) => {
    setExpandedTile((prev) => (prev === id ? null : id));
  };

  useClickOutside([walletAddressRef], () => toggleDropdown(null));

  useEffect(() => {
    const handleClickOutside = () => setIsFilterModalOpen(false);

    if (isFilterModalOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => document.removeEventListener('click', handleClickOutside);
  }, [isFilterModalOpen]);

  useEffect(() => {
    const handleClickOutside = () => setIsSortingModalOpen(false);

    if (isSortingModalOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => document.removeEventListener('click', handleClickOutside);
  }, [isSortingModalOpen]);

  const handleFilterChange = (filterKey, value) => {
    setEmailFilter((prevFilter) => {
      const newFilter = { ...prevFilter };

      // If the filter is for Inbox or SentItems, remove the other
      if (filterKey === 'Inbox' || filterKey === 'SentItems') {
        const otherKey = filterKey === 'Inbox' ? 'SentItems' : 'Inbox';
        delete newFilter[otherKey];
      }

      // Toggle the current filter
      if (newFilter[filterKey] === value) {
        delete newFilter[filterKey];
      } else {
        newFilter[filterKey] = value;
      }
      return newFilter;
    });
  };

  useEffect(() => {
    if (selectedTab === 'teams' || selectedTab === 'meeting') {
      setShowPremium(true);
      const timeout = setTimeout(() => {
        setShowPremium(false);
      }, 1000);

      return () => clearTimeout(timeout); // cleanup on unmount or tab change
    } else {
      setShowPremium(false); // no premium popup for other tabs
    }
  }, [selectedTab]);

  return (
    <>
      {/* ################################################### Mail Boxes Integration Component START ###############################################  */}
      {showIntegration && (
        <Integration
          onClose={() => setShowIntegration(false)}
          user_google_id={userGoogleId}
          onlyEmailApps={true}
        />
      )}
      {/* ################################################### Mail Boxes Integration Component END ###############################################  */}

      <div
        className={` relative flex ${selectedTab === 'mails' ? 'w-full' : 'w-5xl'} mt-5 items-center justify-between sm:mt-[40px] md:mt-2 `}
      >
        {/* left Aligned buttons */}

        {selectedTab === 'mails' &&
          (isGmailAuthenticated || isOutlookAuthenticated) &&
          !selectedEmail && (
            <div className="h-[28px} absolute -bottom-2 left-0 flex gap-2 md:h-[28px] ">
              <MailBoxesIntegration
                isOutlook={isOutlook}
                setIsOutlook={setIsOutlook}
                isGmailAuthenticated={isGmailAuthenticated}
                isOutlookAuthenticated={isOutlookAuthenticated}
              />

              {/* Search Button */}
              {((isOutlook && isOutlookSynchronized) ||
                (!isOutlook && isGmailSynchronized)) && (
                <button
                  onClick={() => setIsSearchBoxOpen(!isSearchBoxOpen)}
                  className="br-1 rounded-t-lg border border-zinc-700 bg-zinc-800 p-2 text-white transition-all duration-200 hover:bg-zinc-700"
                >
                  <FaSearch className="text-[10px] md:text-[15px]" />
                </button>
              )}

              {/* Link Mail Boxes Button */}
              <button
                onClick={() => setShowIntegration(true)}
                className="br-1 group flex items-center justify-center gap-2 rounded-t-lg border border-zinc-700 bg-zinc-800 p-2 text-white transition-all duration-200 hover:bg-zinc-700"
              >
                <FiLink className="text-[10px] md:text-[15px]" />
                <span className="hidden text-xs transition-all duration-300 group-hover:block ">
                  Integrate Mail Boxes
                </span>
              </button>
            </div>
          )}

        {selectedTab === 'meeting' && showPremium && (
          <div className="br-1 absolute -bottom-2 left-0 mx-auto flex w-fit gap-1 rounded-t-xl border border-zinc-700 bg-zinc-800 p-1 text-sm">
            {/* Completed Button */}
            <button
              title="Completed"
              className={`rounded-lg px-3  py-1  text-white transition-all  duration-200  hover:border-gray-300 hover:bg-zinc-700 
                  ${meetingSubTab === 'completed' ? 'bg-primary-gradient' : ''}`}
              onClick={() => setMeetingSubTab('completed')}
            >
              Completed
            </button>

            {/* Upcoming Button */}
            <button
              title="Upcoming"
              className={`rounded-lg px-3 py-1  text-white transition-all  duration-200  hover:border-gray-300 hover:bg-zinc-700 
                  ${meetingSubTab === 'upcoming' ? 'bg-primary-gradient' : ''}`}
              onClick={() => setMeetingSubTab('upcoming')}
            >
              Upcoming
            </button>
          </div>
        )}

        {/* Centered Tab Buttons */}
        <div
          className="pointer-events-none flex w-full opacity-0 md:pointer-events-auto md:opacity-100"
          style={{ fontSize: '0.6rem' }}
        >
          <div className="mx-auto flex w-fit gap-1 rounded-full bg-transparent p-1 py-2"></div>
        </div>

        {/* Right-aligned buttons */}
        {selectedTab === 'mails' &&
          (isGmailAuthenticated || isOutlookAuthenticated) &&
          !selectedEmail &&
          ((isOutlook && isOutlookSynchronized) ||
            (!isOutlook && isGmailSynchronized)) && (
            <>
              <div className="absolute -bottom-[10px] right-0 hidden items-center gap-1 md:flex">
                <div className="br-1 mx-auto flex w-fit gap-1 rounded-t-xl border border-zinc-700 bg-zinc-800 p-1 text-sm">
                  {/* Inbox Button */}
                  <button
                    title="Inbox"
                    className={`rounded-lg  px-1  text-white transition-all  duration-200  hover:border-gray-300 hover:bg-zinc-700 
                  ${emailfilter['Inbox'] ? 'bg-primary-gradient' : ''}`}
                    onClick={() => handleFilterChange('Inbox', 'Inbox')}
                  >
                    <img src="/assets/inbox.png" alt="inbox" className="w-6" />
                  </button>

                  {/* Sent Button */}
                  <button
                    title="Sent Items"
                    className={`rounded-lg  px-1  text-white transition-all  duration-200  hover:border-gray-300 hover:bg-zinc-700 
                  ${emailfilter['SentItems'] ? 'bg-primary-gradient' : ''}`}
                    onClick={() => handleFilterChange('SentItems', 'SentItems')}
                  >
                    <img src="/assets/sent.png" alt="sent" className="w-6" />
                  </button>
                </div>

                {/* eslint-disable-next-line */}
                <div className="relative" onClick={(e) => e.stopPropagation()}>
                  {/* compose an email Button */}
                  <button
                    onClick={() => {
                      setIsComposeEmailModalOpen(!isComposeEmailModalOpen);
                      setIsFilterModalOpen(false); // Close Filter modal
                      setIsSortingModalOpen(false); // Close Sorting modal
                      setIsAdvancedSearchOpen(false);
                    }}
                    className="br-1 rounded-t-lg border border-zinc-700 bg-zinc-800 p-2 text-white transition-all duration-200 hover:bg-zinc-700"
                  >
                    <RiMailAddFill className="text-[10px] md:text-[15px]" />
                  </button>

                  {/* compose an email Modal */}
                  {isComposeEmailModalOpen && (
                    <ComposeEmail
                      setIsComposeEmailModalOpen={setIsComposeEmailModalOpen}
                      message_id="12"
                      isOutlook={isOutlook}
                    />
                  )}
                </div>

                {/* eslint-disable-next-line */}
                <div
                  className="relative inline-block"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Filter Button */}
                  <button
                    onClick={() => setIsFilterModalOpen(!isFilterModalOpen)}
                    className="br-1 rounded-t-lg border border-zinc-700 bg-zinc-800 p-2 text-white transition-all duration-200 hover:bg-zinc-700"
                  >
                    <FaFilter className="text-[10px] md:text-[15px]" />
                  </button>

                  {/* Dropdown Modal */}
                  {isFilterModalOpen && (
                    <div className="absolute right-0 z-[60] mt-2   transform rounded-lg  border-[1px] border-gray-600 bg-zinc-800 p-4 shadow-lg ">
                      <EmailLabels
                        emailStats={emailStats}
                        emailfilter={emailfilter}
                        setEmailFilter={setEmailFilter}
                        handleFilterChange={handleFilterChange}
                      />
                    </div>
                  )}
                </div>

                {/* eslint-disable-next-line */}
                <div
                  className="relative inline-block"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setIsSortingModalOpen(!isSortingModalOpen)}
                    className="br-1 rounded-t-lg border border-zinc-700 bg-zinc-800 p-2 text-white transition-all duration-200 hover:bg-zinc-700"
                  >
                    <PiSortAscendingBold size={15} />
                  </button>

                  {/* Dropdown Modal */}
                  {isSortingModalOpen && (
                    <div className="absolute right-0  z-[60] mt-2   transform rounded-lg  border-[1px] border-gray-600 bg-zinc-900 p-4 shadow-lg ">
                      <EmailSorting
                        emailSorting={emailSorting}
                        setEmailSorting={setEmailSorting}
                        emailfilter={emailfilter}
                        setEmailFilter={setEmailFilter}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="absolute -bottom-2 right-0 w-full md:hidden">
                <div className="relative w-full">
                  <button
                    onClick={() => setIsMobileDrawerOpen(!isMobileDrawerOpen)}
                    className="absolute bottom-0 right-0 rounded-lg border border-zinc-600 bg-zinc-800 p-2 text-white"
                  >
                    {isMobileDrawerOpen ? (
                      <IoClose size={10} />
                    ) : (
                      <TiThMenu size={10} />
                    )}
                  </button>

                  {/* Drawer with buttons (only on mobile) */}
                  {isMobileDrawerOpen && (
                    <div className="absolute right-0 z-50 mt-0 flex w-fit md:hidden">
                      <div className="relative flex w-fit flex-col items-center justify-around gap-2 rounded-lg border border-zinc-700 bg-zinc-900 p-2 shadow-lg">
                        {/* Same buttons you have */}
                        <button
                          title="Inbox"
                          onClick={() => handleFilterChange('Inbox', 'Inbox')}
                          className={`w-full rounded-lg px-3 text-white hover:bg-zinc-700 ${emailfilter['Inbox'] ? 'bg-primary-gradient' : ''}`}
                        >
                          <img
                            src="/assets/inbox.png"
                            alt="inbox"
                            className="w-5"
                          />
                        </button>

                        <button
                          title="Sent"
                          onClick={() =>
                            handleFilterChange('SentItems', 'SentItems')
                          }
                          className={`rounded-lg px-3 text-white hover:bg-zinc-700 ${emailfilter['SentItems'] ? 'bg-primary-gradient' : ''}`}
                        >
                          <img
                            src="/assets/sent.png"
                            alt="sent"
                            className="w-5"
                          />
                        </button>

                        <button
                          onClick={() => {
                            setIsComposeEmailModalOpen(
                              !isComposeEmailModalOpen,
                            );
                            setIsmdFilterModalOpen(false);
                            setIsmdSortingModalOpen(false);
                            setIsAdvancedSearchOpen(false);
                          }}
                          className="rounded-lg border border-zinc-700 bg-zinc-800 p-2 text-white hover:bg-zinc-700"
                        >
                          <RiMailAddFill size={10} />
                        </button>

                        <button
                          onClick={() =>
                            setIsmdFilterModalOpen(!ismdFilterModalOpen)
                          }
                          className="rounded-lg border border-zinc-700 bg-zinc-800 p-2 text-white hover:bg-zinc-700"
                        >
                          <FaFilter size={10} />
                        </button>

                        {/* Dropdown Modal */}
                        {ismdFilterModalOpen && (
                          <div className="absolute right-16 top-0  z-[60]   transform rounded-lg border-[1px]  border-gray-600 bg-zinc-800 p-2 shadow-lg sm:p-4 ">
                            <EmailLabels
                              emailStats={emailStats}
                              emailfilter={emailfilter}
                              setEmailFilter={setEmailFilter}
                              handleFilterChange={handleFilterChange}
                            />
                          </div>
                        )}

                        <button
                          onClick={() =>
                            setIsmdSortingModalOpen(!ismdSortingModalOpen)
                          }
                          className="rounded-lg border border-zinc-700 bg-zinc-800 p-2 text-white hover:bg-zinc-700"
                        >
                          <PiSortAscendingBold size={10} />
                        </button>

                        {/* Dropdown Modal */}
                        {ismdSortingModalOpen && (
                          <div className="absolute right-16 top-0 z-[60]   transform rounded-lg border-[1px]  border-gray-600 bg-zinc-900 p-2 shadow-lg sm:p-4 ">
                            <EmailSorting
                              emailSorting={emailSorting}
                              setEmailSorting={setEmailSorting}
                              emailfilter={emailfilter}
                              setEmailFilter={setEmailFilter}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
      </div>
      <div
        className={` flex-1 ${!showPremium && 'hide-sidebar overflow-y-auto'} `}
      >
        <div className="flex h-full flex-1 flex-col">
          {/* ################## TEAMS TAB ###############*/}
          {selectedTab === 'teams' &&
            (!showPremium ? (
              <PremiumFeatures setShowPremium={setShowPremium} />
            ) : (
              <div className="hide-sidebar flex h-full flex-col gap-5 overflow-y-auto">
                {dialogueData.map((item) => (
                  <div
                    key={item.id}
                    className="br-2 bg-2 text-p-text relative flex flex-col gap-2 rounded-md border px-4 py-3 transition-colors duration-150 ease-in-out"
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1">
                        <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded bg-white p-0.5">
                          <img src={item.app.image} alt="Trigger icon" />
                        </div>
                        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-white p-0.5">
                          <img
                            src={item.app.userImage}
                            alt="Trigger icon"
                            className="rounded-full"
                          />
                        </div>
                      </div>
                      <strong className="text-md flex-1 truncate font-medium">
                        {item.app.userName}
                      </strong>
                      <div className="relative z-10 mb-3 flex items-center gap-4">
                        <button
                          type="button"
                          onClick={() => toggleDropdown(item.id)}
                          className="text-md text-p-text -mr-1 flex items-center rounded-[3px] border-0 bg-transparent px-1 py-1 font-semibold transition-colors duration-150 ease-in-out disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">
                            Open workflow settings
                          </span>
                          <PiDotsThreeOutlineVerticalFill className="text-xl" />
                        </button>
                      </div>
                    </div>
                    {dropdownOpen === item.id && (
                      <div
                        className="bg-1 br-1 text-s-text absolute right-2 z-20 mt-8 w-48 rounded-xl border p-2"
                        ref={walletAddressRef}
                      >
                        <button className="bg-1-hover block w-full px-4 py-2 text-left text-sm">
                          Share
                        </button>
                        <button className="bg-1-hover block w-full px-4 py-2 text-left text-sm">
                          Delete
                        </button>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <strong className="text-md font-medium">
                        {item.query || 'No query provided'}
                      </strong>
                      <button
                        type="button"
                        onClick={() => toggleExpand(item.id)}
                        className={`text-md transform rounded-[3px] px-4 text-xl font-semibold transition-all duration-300 ease-in-out disabled:cursor-not-allowed ${
                          expandedTile === item.id ? 'rotate-180' : 'rotate-0'
                        }`}
                      >
                        <IoIosArrowDown />
                      </button>
                    </div>

                    {/* AI Answer */}
                    <p className="text-sm text-gray-600">
                      {item.answer || 'No answer provided by AI'}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="mt-3 flex justify-center gap-2">
                        {item.data_sources.map((source) => (
                          <div
                            key={source.id}
                            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-white p-0.5"
                          >
                            <img src={source.image} alt="Trigger icon" />
                          </div>
                        ))}
                      </div>
                      <button className="bg-1 rounded-lg px-3 py-1">
                        Approve
                      </button>
                    </div>
                    {expandedTile === item.id ? (
                      <div className="mt-3">
                        {item.data_sources.length > 0 ? (
                          <div className="grid gap-2">
                            {item.data_sources.map((source, index) => (
                              <div
                                key={source.id}
                                className="br-1 bg-1 flex flex-col items-start rounded-lg border px-4 py-2"
                              >
                                <div className="flex items-center gap-2">
                                  <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-white p-0.5">
                                    <img
                                      src={source.image}
                                      alt="Trigger icon"
                                    />
                                  </div>
                                  <span className="font-semibold">
                                    {source.label}
                                  </span>
                                </div>
                                <div className="mt-3">
                                  <p className="text-sm text-zinc-400">
                                    {source.fileName} ·{' '}
                                    {taskDateFormat(source.timestamp)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p>No data sources available</p>
                        )}
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                ))}
              </div>
            ))}

          {/* ################## MAILS TAB ###############*/}
          {selectedTab === 'mails' &&
            (!isGmailAuthenticated && !isOutlookAuthenticated ? (
              <NoMailScreen
                noneAuthenticated={true}
                isGmailSynchronized
                isOutlookSynchronized
              />
            ) : selectedEmail ? (
              <EmailComponent
                isOutlook={isOutlook}
                messageId={selectedEmail.id}
                analytics={analyticsData}
                onClose={() => setSelectedEmail(null)}
                isDialog={true}
                isAnalyticsView={isAnalyticsView}
                setIsAnalyticsView={setIsAnalyticsView}
              />
            ) : (
              <div className="flex h-full flex-col transition-all duration-300 ">
                {/* Search input box with smooth transition */}
                <div
                  // className={`transition-all duration-300 ${isSearchBoxOpen ? "max-h-40 opacity-100" : "max-h-0 opacity-0 hidden"
                  className={`transition-all duration-300 ${
                    isSearchBoxOpen
                      ? 'sticky top-0 z-10 max-h-40 bg-zinc-900 opacity-100'
                      : 'hidden max-h-0 opacity-0'
                  }`}
                >
                  <EmailSearch
                    isSearchBoxOpen={isSearchBoxOpen}
                    setIsSearchBoxOpen={setIsSearchBoxOpen}
                    setIsAdvancedSearchOpen={setIsAdvancedSearchOpen}
                    isAdvanceSearchOpen={isAdvanceSearchOpen}
                    isOutlook={isOutlook}
                  />
                </div>

                {/* Email List - Will be pushed down smoothly */}
                <EmailList
                  emailList={emails}
                  totalEmails={totalEmails}
                  emailsPerPage={emailsPerPage}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  setSelectedEmail={setSelectedEmail}
                  loadingEmails={loadingEmails}
                  isOutlook={isOutlook}
                  emailfilter={emailfilter}
                  handleFilterChange={handleFilterChange}
                  className="w-full"
                  isGmailSynchronized={isGmailSynchronized}
                  isOutlookSynchronized={isOutlookSynchronized}
                />
              </div>
            ))}

          {/* ################## MEETING TAB ###############*/}
          {selectedTab === 'meeting' &&
            (!showPremium ? (
              <PremiumFeatures setShowPremium={setShowPremium} />
            ) : (
              <div className="hide-sidebar flex max-h-full flex-col items-center gap-5 overflow-y-auto">
                {meetingSubTab === 'completed' && (
                  <>
                    {meetingFeedbackData.map((data, index) => (
                      <CombinedFeedback meetingData={data} key={index} />
                    ))}
                  </>
                )}

                {meetingSubTab === 'upcoming' && (
                  <>
                    <CalendarEventEditor />
                  </>
                )}
              </div>
            ))}
        </div>
      </div>

      {/* Pagination controls */}
      {selectedTab === 'mails' &&
        !selectedEmail &&
        (isGmailAuthenticated || isOutlookAuthenticated) &&
        ((isOutlook && isOutlookSynchronized) ||
          (!isOutlook && isGmailSynchronized)) && (
          <>
            <div className="mx-auto flex w-full items-center justify-between gap-1 rounded-xl bg-zinc-800 p-1 px-4 text-zinc-200">
              {loadingEmails ? (
                <div className="flex w-full items-center justify-center">
                  <div className="h-6 w-6 animate-pulse rounded-full bg-zinc-400"></div>
                </div>
              ) : (
                <>
                  <button
                    onClick={prevPage}
                    disabled={currentPage === 1}
                    className={`rounded-lg px-2 py-1 ${
                      currentPage === 1
                        ? 'cursor-not-allowed opacity-50'
                        : 'hover:text-zinc-400'
                    }`}
                  >
                    <FaAngleLeft />
                  </button>
                  <span className="text-sm font-semibold">
                    Page {currentPage} of {totalPages === 0 ? '1' : totalPages}
                  </span>
                  <button
                    onClick={nextPage}
                    disabled={currentPage === totalPages}
                    className={`rounded-lg px-2 py-1 ${
                      currentPage === totalPages
                        ? 'cursor-not-allowed opacity-50'
                        : 'hover:text-zinc-400'
                    }`}
                  >
                    <FaAngleRight />
                  </button>
                </>
              )}
            </div>
          </>
        )}
    </>
  );
};

const Dialog = () => {
  const { userInfo } = DataState();
  const [openCreate, setOpenCreate] = useState(false);
  const [isAnalyticsView, setIsAnalyticsView] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isComposeEmailModalOpen, setIsComposeEmailModalOpen] = useState(false);
  const { isOutlook, refreshDialogKey } = useEmails();

  const openCreateModal = () => {
    switch (selectedTab) {
      case 'mails':
        return setIsComposeEmailModalOpen(true);
      case 'teams':
        return setOpenCreate(true);
      case 'meeting':
        return setOpenCreate(true);
      default:
        return setOpenCreate(true);
    }
  };
  const closeCreateModal = () => {
    switch (selectedTab) {
      case 'mails':
        return setIsComposeEmailModalOpen(false);
      case 'teams':
        return setOpenCreate(false);
      case 'meeting':
        return setOpenCreate(false);
      default:
        return setOpenCreate(false);
    }
  };
  const { emailStats, setEmailStats } = useEmails();
  const [selectedTab, setSelectedTab] = useState('mails');
  const handleSelectTab = (tab) => {
    setSelectedTab(tab);
  };

  const getCreateName = () => {
    switch (selectedTab) {
      case 'mails':
        return 'New Email';
      case 'teams':
        return 'New Team';
      case 'meeting':
        return 'New Meeting';
      default:
        return 'Create New';
    }
  };

  return (
    <BaseLayout
      createName={getCreateName()}
      handleCreate={openCreateModal}
      tabName="communication"
      leftSide={<></>}
    >
      {/* <div className="flex flex-col w-full h-full max-w-4xl gap-3 px-1 mx-auto"> */}
      <div className="flex h-full w-full flex-col gap-3 overflow-hidden px-1 ">
        {false ? (
          <div className="mx-auto flex h-full w-full max-w-4xl flex-col gap-3">
            {userInfo && (
              <div className="flex items-center gap-2">
                <img
                  src={userInfo.picture}
                  alt="User Avatar"
                  className="h-8 w-8 rounded-full object-cover"
                />
                <span className="text-sm">{userInfo.name}</span>
              </div>
            )}
            <div className="flex flex-1 flex-col items-center justify-center gap-10">
              <div className="w-full">
                <h1 className="text-5xl font-bold leading-snug">
                  <span className="gradient-text mr-2 font-bold">Namaste!</span>
                  I'm Supermanager AI
                  <br />
                  Let’s Me Automate your Communication and Response to Queries
                </h1>
                <h2 className="text-3xl text-zinc-500">Create your Dialogue</h2>
              </div>
            </div>
          </div>
        ) : (
          <DialogueDetails
            key={refreshDialogKey}
            setEmailStats={setEmailStats}
            handleSelectTab={handleSelectTab}
            selectedTab={selectedTab}
            isAnalyticsView={isAnalyticsView}
            setIsAnalyticsView={setIsAnalyticsView}
            emailStats={emailStats}
            selectedEmail={selectedEmail}
            setSelectedEmail={setSelectedEmail}
            isComposeEmailModalOpen={isComposeEmailModalOpen}
            setIsComposeEmailModalOpen={setIsComposeEmailModalOpen}
          />
        )}
      </div>
      <Modal
        title="Create Dialogue"
        isOpen={openCreate}
        onClose={closeCreateModal}
      >
        <CreateDialogueForm />
      </Modal>
      {isComposeEmailModalOpen && (
        <ComposeEmail
          setIsComposeEmailModalOpen={setIsComposeEmailModalOpen}
          message_id="12"
          isOutlook={isOutlook}
        />
      )}
    </BaseLayout>
  );
};

export default Dialog;
